import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { MainLayout } from './components/layout/MainLayout.js';
import { AuthLayout } from './components/layout/AuthLayout.js';
import { ProtectedRoute } from './components/common/ProtectedRoute.js';
import { QAOverlay } from './components/preview/QAOverlay.js';

// LoginPage stays eager — it's the cold-boot entry point for unauthenticated
// users; rendering through Suspense would add a spinner flash on load.
import { LoginPage } from './pages/auth/LoginPage.js';

// Roadmap #97 — every other route is split off the index bundle. Auth helper
// pages, user/group/notes/files/memory CRUDs all lazy-load on first nav.
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage.js').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage.js').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage.js').then((m) => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.js').then((m) => ({ default: m.VerifyEmailPage })));
const OAuthCallbackPage = lazy(() => import('./pages/auth/OAuthCallbackPage.js').then((m) => ({ default: m.OAuthCallbackPage })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));
const UsersListPage = lazy(() => import('./pages/users/UsersListPage.js').then((m) => ({ default: m.UsersListPage })));
const UserFormPage = lazy(() => import('./pages/users/UserFormPage.js').then((m) => ({ default: m.UserFormPage })));
const GroupsListPage = lazy(() => import('./pages/groups/GroupsListPage.js').then((m) => ({ default: m.GroupsListPage })));
const GroupFormPage = lazy(() => import('./pages/groups/GroupFormPage.js').then((m) => ({ default: m.GroupFormPage })));
const NotesPage = lazy(() => import('./pages/notes/NotesPage.js').then((m) => ({ default: m.NotesPage })));
const FilesPage = lazy(() => import('./pages/files/FilesPage.js').then((m) => ({ default: m.FilesPage })));
const MemoryListPage = lazy(() => import('./pages/memory/MemoryListPage.js').then((m) => ({ default: m.MemoryListPage })));
const MemoryFormPage = lazy(() => import('./pages/memory/MemoryFormPage.js').then((m) => ({ default: m.MemoryFormPage })));

const AIPage = lazy(() => import('./pages/ai/AIPage.js'));
const AISettingsPage = lazy(() => import('./pages/ai/AISettingsPage.js'));
const AIHubLayout = lazy(() => import('./pages/ai/AIHubLayout.js'));
const AIHubDashboard = lazy(() => import('./pages/ai/AIHubDashboard.js'));
const AIProfileList = lazy(() => import('./pages/ai/AIProfileList.js'));
const AIProfileEditor = lazy(() => import('./pages/ai/AIProfileEditor.js'));
const AIMemoryPage = lazy(() => import('./pages/ai/AIMemoryPage.js'));
const AITasksPage = lazy(() => import('./pages/ai/AITasksPage.js'));
const AIAuditPage = lazy(() => import('./pages/ai/AIAuditPage.js'));
const AIIssuesPage = lazy(() => import('./pages/ai/AIIssuesPage.js'));
const AIIssueDetailPage = lazy(() => import('./pages/ai/AIIssueDetailPage.js'));
const CopilotMemoryListPage = lazy(() => import('./pages/copilotMemory/CopilotMemoryListPage.js'));
const CopilotMemoryEditPage = lazy(() => import('./pages/copilotMemory/CopilotMemoryEditPage.js'));
const PipelinesPage = lazy(() => import('./pages/pipeline/PipelinesPage.js'));
const PipelineEditorPage = lazy(() => import('./pages/pipeline/PipelineEditorPage.js'));
const PipelineTemplatesPage = lazy(() => import('./pages/pipeline/PipelineTemplatesPage.js'));
const AINotificationsPage = lazy(() => import('./pages/ai/AINotificationsPage.js'));
const CanvasPage = lazy(() => import('./pages/canvas/CanvasPage.js'));
const CanvasEditorPage = lazy(() => import('./pages/canvas/CanvasEditorPage.js'));
const CanvasPublicViewPage = lazy(() => import('./pages/canvas/CanvasPublicViewPage.js'));
const UISystemPage = lazy(() => import('./pages/uiSystem/UISystemPage.js'));
const SystemPage = lazy(() => import('./pages/system/SystemPage.js'));
const OpenCodePage = lazy(() => import('./pages/opencode/OpenCodePage.js'));
const FileEditorPage = lazy(() => import('./pages/files/FileEditorPage.js'));
const ClaudeCliSessionListPage = lazy(() => import('./pages/claudeCli/SessionListPage.js'));
const ClaudeCliSessionViewPage = lazy(() => import('./pages/claudeCli/SessionViewPage.js'));
const ClaudeCliSpawnPage = lazy(() => import('./pages/claudeCli/SpawnPage.js'));
const SecurityPage = lazy(() => import('./pages/settings/SecurityPage.js'));
const EvalProfilePage = lazy(() => import('./pages/ai/eval/EvalProfilePage.js'));
const PreviewAdminPage = lazy(() => import('./pages/preview/PreviewAdminPage.js'));

// Atlas pages
const AtlasOverviewPage = lazy(() => import('./pages/ai/atlas/AtlasOverviewPage.js'));
const AtlasGraphPage = lazy(() => import('./pages/ai/atlas/AtlasGraphPage.js'));
const AtlasModulesPage = lazy(() => import('./pages/ai/atlas/AtlasModulesPage.js'));
const AtlasNodeDetailPage = lazy(() => import('./pages/ai/atlas/AtlasNodeDetailPage.js'));
const AtlasDataModelPage = lazy(() => import('./pages/ai/atlas/AtlasDataModelPage.js'));
const AtlasToolsPage = lazy(() => import('./pages/ai/atlas/AtlasToolsPage.js'));

// Roadmap pages
const RoadmapDashboardPage = lazy(() => import('./pages/ai/roadmap/RoadmapDashboardPage.js'));
const RoadmapInboxPage = lazy(() => import('./pages/ai/roadmap/RoadmapInboxPage.js'));
const RoadmapKanbanPage = lazy(() => import('./pages/ai/roadmap/RoadmapKanbanPage.js'));
const RoadmapMatrixPage = lazy(() => import('./pages/ai/roadmap/RoadmapMatrixPage.js'));
const RoadmapTechTreePage = lazy(() => import('./pages/ai/roadmap/RoadmapTechTreePage.js'));
const RoadmapItemDetailPage = lazy(() => import('./pages/ai/roadmap/RoadmapItemDetailPage.js'));
const RoadmapTimelinePage = lazy(() => import('./pages/ai/roadmap/RoadmapTimelinePage.js'));

// Activity feed page
const FeedPage = lazy(() => import('./pages/ai/feed/FeedPage.js'));

// Releases page
const ReleasesPage = lazy(() => import('./pages/ai/releases/ReleasesPage.js'));

// Email Admin pages (#142)
const EmailAdminLayout = lazy(() => import('./pages/emailAdmin/EmailAdminLayout.js'));
const EmailProfilesPage = lazy(() => import('./pages/emailAdmin/EmailProfilesPage.js'));
const EmailAddressesPage = lazy(() => import('./pages/emailAdmin/EmailAddressesPage.js'));
const EmailTemplatesPage = lazy(() => import('./pages/emailAdmin/EmailTemplatesPage.js'));
const EmailLogsPage = lazy(() => import('./pages/emailAdmin/EmailLogsPage.js'));
const EmailSettingsPage = lazy(() => import('./pages/emailAdmin/EmailSettingsPage.js'));

function SuspenseFallback() {
  return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" /></div>;
}

export default function App() {
  return (
    // Outer Suspense covers every lazy route. Inner per-route Suspense
    // wrappers (kept for backward-compat) just shadow this one — no harm.
    <Suspense fallback={<SuspenseFallback />}>
    <QAOverlay />
    <Routes>
      {/* Public canvas share route (no auth) */}
      <Route path="/canvas/public/:token" element={<Suspense fallback={<SuspenseFallback />}><CanvasPublicViewPage /></Suspense>} />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/users" element={<UsersListPage />} />
          <Route path="/users/new" element={<UserFormPage />} />
          <Route path="/users/:id/edit" element={<UserFormPage />} />
          <Route path="/groups" element={<GroupsListPage />} />
          <Route path="/groups/new" element={<GroupFormPage />} />
          <Route path="/groups/:id/edit" element={<GroupFormPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/notes/:id" element={<NotesPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/files/:id/edit" element={<Suspense fallback={<SuspenseFallback />}><FileEditorPage /></Suspense>} />
          <Route path="/canvas" element={<Suspense fallback={<SuspenseFallback />}><CanvasPage /></Suspense>} />
          <Route path="/canvas/:id" element={<Suspense fallback={<SuspenseFallback />}><CanvasEditorPage /></Suspense>} />
          <Route path="/design-system" element={<Suspense fallback={<SuspenseFallback />}><UISystemPage /></Suspense>} />
          <Route path="/ui-system" element={<Navigate to="/design-system" replace />} />
          <Route path="/memory" element={<MemoryListPage />} />
          <Route path="/memory/new" element={<MemoryFormPage />} />
          <Route path="/memory/:key/edit" element={<MemoryFormPage />} />
          <Route path="/system" element={<Suspense fallback={<SuspenseFallback />}><SystemPage /></Suspense>} />
          <Route path="/settings/security" element={<Suspense fallback={<SuspenseFallback />}><SecurityPage /></Suspense>} />
          <Route path="/preview" element={<Suspense fallback={<SuspenseFallback />}><PreviewAdminPage /></Suspense>} />
          <Route path="/ai/eval/profile/:id" element={<Suspense fallback={<SuspenseFallback />}><EvalProfilePage /></Suspense>} />
          <Route path="/opencode" element={<Suspense fallback={<SuspenseFallback />}><OpenCodePage /></Suspense>} />
          {/* AI Hub */}
          <Route path="/ai" element={<Suspense fallback={<SuspenseFallback />}><AIHubLayout /></Suspense>}>
            <Route index element={<Suspense fallback={<SuspenseFallback />}><AIHubDashboard /></Suspense>} />
            <Route path="profiles" element={<Suspense fallback={<SuspenseFallback />}><AIProfileList /></Suspense>} />
            <Route path="profiles/new" element={<Suspense fallback={<SuspenseFallback />}><AIProfileEditor /></Suspense>} />
            <Route path="profiles/:id" element={<Suspense fallback={<SuspenseFallback />}><AIProfileEditor /></Suspense>} />
            <Route path="chat" element={<Suspense fallback={<SuspenseFallback />}><AIPage /></Suspense>} />
            <Route path="chat/:id" element={<Suspense fallback={<SuspenseFallback />}><AIPage /></Suspense>} />
            <Route path="issues" element={<Suspense fallback={<SuspenseFallback />}><AIIssuesPage /></Suspense>} />
            <Route path="issues/new" element={<Suspense fallback={<SuspenseFallback />}><AIIssueDetailPage /></Suspense>} />
            <Route path="issues/:id" element={<Suspense fallback={<SuspenseFallback />}><AIIssueDetailPage /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<SuspenseFallback />}><AINotificationsPage /></Suspense>} />
            <Route path="memory" element={<Suspense fallback={<SuspenseFallback />}><AIMemoryPage /></Suspense>} />
            <Route path="tasks" element={<Suspense fallback={<SuspenseFallback />}><AITasksPage /></Suspense>} />
            <Route path="audit" element={<Suspense fallback={<SuspenseFallback />}><AIAuditPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<SuspenseFallback />}><AISettingsPage /></Suspense>} />
            <Route path="pipelines" element={<Suspense fallback={<SuspenseFallback />}><PipelinesPage /></Suspense>} />
            <Route path="pipelines/new" element={<Suspense fallback={<SuspenseFallback />}><PipelineEditorPage /></Suspense>} />
            <Route path="pipelines/templates" element={<Suspense fallback={<SuspenseFallback />}><PipelineTemplatesPage /></Suspense>} />
            <Route path="pipelines/:id" element={<Suspense fallback={<SuspenseFallback />}><PipelineEditorPage /></Suspense>} />
            {/* Atlas routes */}
            <Route path="atlas" element={<Suspense fallback={<SuspenseFallback />}><AtlasOverviewPage /></Suspense>} />
            <Route path="atlas/graph" element={<Suspense fallback={<SuspenseFallback />}><AtlasGraphPage /></Suspense>} />
            <Route path="atlas/modules" element={<Suspense fallback={<SuspenseFallback />}><AtlasModulesPage /></Suspense>} />
            <Route path="atlas/data-model" element={<Suspense fallback={<SuspenseFallback />}><AtlasDataModelPage /></Suspense>} />
            <Route path="atlas/tools" element={<Suspense fallback={<SuspenseFallback />}><AtlasToolsPage /></Suspense>} />
            <Route path="atlas/nodes/:id" element={<Suspense fallback={<SuspenseFallback />}><AtlasNodeDetailPage /></Suspense>} />
            {/* Roadmap routes */}
            <Route path="roadmap" element={<Suspense fallback={<SuspenseFallback />}><RoadmapDashboardPage /></Suspense>} />
            <Route path="roadmap/inbox" element={<Suspense fallback={<SuspenseFallback />}><RoadmapInboxPage /></Suspense>} />
            <Route path="roadmap/kanban" element={<Suspense fallback={<SuspenseFallback />}><RoadmapKanbanPage /></Suspense>} />
            <Route path="roadmap/matrix" element={<Suspense fallback={<SuspenseFallback />}><RoadmapMatrixPage /></Suspense>} />
            <Route path="roadmap/tech-tree" element={<Suspense fallback={<SuspenseFallback />}><RoadmapTechTreePage /></Suspense>} />
            <Route path="roadmap/timeline" element={<Suspense fallback={<SuspenseFallback />}><RoadmapTimelinePage /></Suspense>} />
            <Route path="roadmap/items/:id" element={<Suspense fallback={<SuspenseFallback />}><RoadmapItemDetailPage /></Suspense>} />
            {/* Activity feed */}
            <Route path="feed" element={<Suspense fallback={<SuspenseFallback />}><FeedPage /></Suspense>} />
            {/* Releases */}
            <Route path="releases" element={<Suspense fallback={<SuspenseFallback />}><ReleasesPage /></Suspense>} />
          </Route>
          {/* Email Admin (#142) */}
          <Route path="/email-admin" element={<Suspense fallback={<SuspenseFallback />}><EmailAdminLayout /></Suspense>}>
            <Route index element={<Navigate to="/email-admin/profiles" replace />} />
            <Route path="profiles" element={<Suspense fallback={<SuspenseFallback />}><EmailProfilesPage /></Suspense>} />
            <Route path="addresses" element={<Suspense fallback={<SuspenseFallback />}><EmailAddressesPage /></Suspense>} />
            <Route path="templates" element={<Suspense fallback={<SuspenseFallback />}><EmailTemplatesPage /></Suspense>} />
            <Route path="logs" element={<Suspense fallback={<SuspenseFallback />}><EmailLogsPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<SuspenseFallback />}><EmailSettingsPage /></Suspense>} />
          </Route>
          <Route path="/claude-cli" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSessionListPage /></Suspense>} />
          <Route path="/claude-cli/spawn" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSpawnPage /></Suspense>} />
          <Route path="/claude-cli/sessions/:uuid" element={<Suspense fallback={<SuspenseFallback />}><ClaudeCliSessionViewPage /></Suspense>} />
          <Route path="/copilot-memory" element={<Suspense fallback={<SuspenseFallback />}><CopilotMemoryListPage /></Suspense>} />
          <Route path="/copilot-memory/new" element={<Suspense fallback={<SuspenseFallback />}><CopilotMemoryEditPage /></Suspense>} />
          <Route path="/copilot-memory/:scope/:filename/edit" element={<Suspense fallback={<SuspenseFallback />}><CopilotMemoryEditPage /></Suspense>} />
        </Route>
      </Route>

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}
